import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const GENERAL_PLACEHOLDER = `[General] Tell the AI what to share with prospects about your studio.

Examples to include:
• About us — when did you start? Who teaches? What makes you different?
• Refund / cancellation policy
• Family / sibling discount details
• Free trial class? How does someone book one?
• Where to park, how to find the studio
• What to bring on day one`;

const offeringPlaceholder = (courseName) => `[This offering: ${courseName || 'this class'}] Add anything specific to this class.

Examples to include:
• What a typical session looks like — warmup, teaching style, cool-down
• Skill level / prerequisites — does anything need to be known beforehand?
• Age range nuances — kids vs adults? Mixed?
• What to wear / what to bring
• What students will be able to do by the end of the program
• Performance, recital, or assessment dates relevant to this offering`;

// Must match Mongoose maxlength on Studio.aiKnowledge + Course.aiKnowledge
// and the Joi cap in middleware/validation.js. Bumped 2026-05-08 from 5000.
const MAX_LEN = 10000;

const AIAssistantTab = () => {
  const { studio, refreshUser } = useAuth();

  const [generalKnowledge, setGeneralKnowledge] = useState('');
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savedGeneral, setSavedGeneral] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [courseDrafts, setCourseDrafts] = useState({});
  const [courseSaving, setCourseSaving] = useState({});
  const [courseSaved, setCourseSaved] = useState({});
  const [courseError, setCourseError] = useState({});

  useEffect(() => {
    setGeneralKnowledge(studio?.aiKnowledge || '');
  }, [studio?.aiKnowledge]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/courses?status=active');
        const list = data.courses || data || [];
        if (cancelled) return;
        setCourses(list);
        const drafts = {};
        for (const c of list) {
          drafts[c._id] = {
            aiKnowledge: c.aiKnowledge || '',
            supportChatEnabled: !!c.supportChatEnabled
          };
        }
        setCourseDrafts(drafts);
      } catch (err) {
        if (!cancelled) setCourseError(prev => ({ ...prev, _list: 'Failed to load courses' }));
      } finally {
        if (!cancelled) setCoursesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const saveGeneral = async () => {
    setSavingGeneral(true);
    setSavedGeneral(false);
    setGeneralError('');
    try {
      await api.put('/studio/ai-knowledge', { aiKnowledge: generalKnowledge });
      await refreshUser();
      setSavedGeneral(true);
    } catch (err) {
      setGeneralError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSavingGeneral(false);
    }
  };

  const updateDraft = (courseId, patch) => {
    setCourseDrafts(prev => ({
      ...prev,
      [courseId]: { ...prev[courseId], ...patch }
    }));
    setCourseSaved(prev => ({ ...prev, [courseId]: false }));
  };

  // Toggle auto-saves immediately. Studios shouldn't have to expand the row
  // and click Save just to flip a chat on/off. Sends only the changed field
  // so it doesn't race with an in-progress aiKnowledge edit. Optimistic UI:
  // flip locally, PUT, revert on error.
  const toggleSupportChat = async (courseId, enabled) => {
    updateDraft(courseId, { supportChatEnabled: enabled });
    setCourseSaving(prev => ({ ...prev, [courseId]: true }));
    setCourseSaved(prev => ({ ...prev, [courseId]: false }));
    setCourseError(prev => ({ ...prev, [courseId]: '' }));
    try {
      await api.put(`/courses/${courseId}`, { supportChatEnabled: enabled });
      setCourses(prev => prev.map(c => c._id === courseId
        ? { ...c, supportChatEnabled: enabled }
        : c
      ));
      setCourseSaved(prev => ({ ...prev, [courseId]: true }));
    } catch (err) {
      // Revert local toggle on failure so the UI matches the server.
      updateDraft(courseId, { supportChatEnabled: !enabled });
      setCourseError(prev => ({
        ...prev,
        [courseId]: err.response?.data?.error || 'Failed to update'
      }));
    } finally {
      setCourseSaving(prev => ({ ...prev, [courseId]: false }));
    }
  };

  const saveCourse = async (courseId) => {
    setCourseSaving(prev => ({ ...prev, [courseId]: true }));
    setCourseSaved(prev => ({ ...prev, [courseId]: false }));
    setCourseError(prev => ({ ...prev, [courseId]: '' }));
    try {
      const draft = courseDrafts[courseId] || {};
      await api.put(`/courses/${courseId}`, {
        aiKnowledge: draft.aiKnowledge || '',
        supportChatEnabled: !!draft.supportChatEnabled
      });
      setCourses(prev => prev.map(c => c._id === courseId
        ? { ...c, aiKnowledge: draft.aiKnowledge || '', supportChatEnabled: !!draft.supportChatEnabled }
        : c
      ));
      setCourseSaved(prev => ({ ...prev, [courseId]: true }));
    } catch (err) {
      setCourseError(prev => ({
        ...prev,
        [courseId]: err.response?.data?.error || 'Failed to save'
      }));
    } finally {
      setCourseSaving(prev => ({ ...prev, [courseId]: false }));
    }
  };

  const toggleExpanded = (courseId) => {
    setExpandedCourseId(prev => prev === courseId ? null : courseId);
  };

  return (
    <div className="space-y-6">
      {/* General studio knowledge */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">AI Assistant — General knowledge</h2>
            <p className="text-sm text-gray-500 mt-1">
              Studio-wide context the AI uses on every offering's chat. Anything written here is read by the assistant on every conversation across your studio.
            </p>
          </div>
          <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand-50 text-brand-600 border border-brand-100">
            [General]
          </span>
        </div>

        {generalError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {generalError}
          </div>
        )}

        <textarea
          rows={10}
          value={generalKnowledge}
          onChange={(e) => {
            if (e.target.value.length <= MAX_LEN) {
              setGeneralKnowledge(e.target.value);
              setSavedGeneral(false);
            }
          }}
          placeholder={GENERAL_PLACEHOLDER}
          className="w-full mt-4 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-y"
        />

        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <span className={`text-xs ${generalKnowledge.length > MAX_LEN - 200 ? 'text-amber-600' : 'text-gray-400'}`}>
            {generalKnowledge.length}/{MAX_LEN} characters
          </span>
          <div className="flex items-center gap-3">
            {savedGeneral && <span className="text-sm text-green-600 font-medium">Saved</span>}
            <button
              onClick={saveGeneral}
              disabled={savingGeneral}
              className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
            >
              {savingGeneral ? 'Saving...' : 'Save general knowledge'}
            </button>
          </div>
        </div>
      </div>

      {/* Per-offering */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">AI Assistant — Per offering</h2>
        <p className="text-sm text-gray-500 mt-1">
          Turn the support chat on for individual offerings and add class-specific knowledge. The AI assistant is off by default for each course — enable it where you'd like prospects to be able to ask questions.
        </p>

        {coursesLoading && (
          <p className="mt-4 text-sm text-gray-400">Loading offerings...</p>
        )}

        {!coursesLoading && courses.length === 0 && (
          <p className="mt-4 text-sm text-gray-400">No active offerings yet. Publish a course first.</p>
        )}

        {!coursesLoading && courses.length > 0 && (
          <div className="mt-4 space-y-3">
            {courses.map(course => {
              const draft = courseDrafts[course._id] || { aiKnowledge: '', supportChatEnabled: false };
              const expanded = expandedCourseId === course._id;
              const isOn = draft.supportChatEnabled;
              return (
                <div key={course._id} className="border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between gap-3 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(course._id)}
                      className="flex-1 min-w-0 text-left flex items-center gap-3"
                    >
                      <span className={`shrink-0 w-2 h-2 rounded-full ${isOn ? 'bg-green-500' : 'bg-gray-300'}`} aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">{course.name}</span>
                        <span className="block text-xs text-gray-500 truncate">
                          {isOn ? 'Chat enabled' : 'Chat disabled'}
                          {course.danceStyle ? ` • ${course.danceStyle}` : ''}
                        </span>
                      </span>
                    </button>
                    <label className="shrink-0 inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={isOn}
                        disabled={!!courseSaving[course._id]}
                        onChange={(e) => toggleSupportChat(course._id, e.target.checked)}
                      />
                      <span className={`w-10 h-6 bg-gray-200 rounded-full peer-checked:bg-brand-500 relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4 ${courseSaving[course._id] ? 'opacity-50' : ''}`} />
                    </label>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(course._id)}
                      className="shrink-0 text-xs text-brand-600 hover:underline"
                    >
                      {expanded ? 'Hide' : 'Edit'}
                    </button>
                  </div>

                  {expanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand-50 text-brand-600 border border-brand-100">
                          [Offering]
                        </span>
                        <span className="text-xs text-gray-500 truncate">Knowledge for {course.name}</span>
                      </div>

                      {courseError[course._id] && (
                        <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          {courseError[course._id]}
                        </div>
                      )}

                      <textarea
                        rows={8}
                        value={draft.aiKnowledge}
                        onChange={(e) => {
                          if (e.target.value.length <= MAX_LEN) {
                            updateDraft(course._id, { aiKnowledge: e.target.value });
                          }
                        }}
                        placeholder={offeringPlaceholder(course.name)}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-y"
                      />

                      <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                        <span className={`text-xs ${(draft.aiKnowledge || '').length > MAX_LEN - 200 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {(draft.aiKnowledge || '').length}/{MAX_LEN} characters
                        </span>
                        <div className="flex items-center gap-3">
                          {courseSaved[course._id] && <span className="text-sm text-green-600 font-medium">Saved</span>}
                          <button
                            type="button"
                            onClick={() => saveCourse(course._id)}
                            disabled={!!courseSaving[course._id]}
                            className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
                          >
                            {courseSaving[course._id] ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistantTab;
